/**
 * RGPD consent store.
 * Persisted in localStorage under 'mylimni_consent'.
 * Consent expires after 365 days and must be re-given.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const STORAGE_KEY = 'mylimni_consent'
const EXPIRY_DAYS = 365

interface PersistedConsent {
  hasResponded: boolean
  analytics: boolean
  consentDate: string | null
}

function loadFromStorage(): PersistedConsent {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { hasResponded: false, analytics: false, consentDate: null }
    const parsed = JSON.parse(raw) as PersistedConsent
    // Check expiry
    if (parsed.consentDate) {
      const age = Date.now() - new Date(parsed.consentDate).getTime()
      if (age > EXPIRY_DAYS * 24 * 60 * 60 * 1000) {
        localStorage.removeItem(STORAGE_KEY)
        return { hasResponded: false, analytics: false, consentDate: null }
      }
    }
    return parsed
  } catch {
    return { hasResponded: false, analytics: false, consentDate: null }
  }
}

/**
 * Lecture synchrone du consentement, sans dépendre de Pinia — utilisée par
 * main.ts avant même l'installation des plugins d'analytics (GTM), pour ne
 * jamais les charger avant que le visiteur ait répondu au bandeau.
 */
export function hasAnalyticsConsent(): boolean {
  return loadFromStorage().analytics
}

function saveToStorage(state: PersistedConsent): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // localStorage might be unavailable (private browsing)
  }
}

export const useConsentStore = defineStore('consent', () => {
  const initial = loadFromStorage()

  const hasResponded = ref(initial.hasResponded)
  const analytics = ref(initial.analytics)
  const consentDate = ref<string | null>(initial.consentDate)

  const needsBanner = computed(() => !hasResponded.value)

  function grantConsent(): void {
    const now = new Date().toISOString()
    hasResponded.value = true
    analytics.value = true
    consentDate.value = now
    saveToStorage({ hasResponded: true, analytics: true, consentDate: now })
    // Le script Tianji (injecté hors de tout bundle JS, cf. vite.config.js) ne
    // se recharge pas seul quand le consentement est donné en cours de
    // session : cet évènement le déclenche sans attendre un rechargement.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('mylimni:analytics-consent-granted'))
    }
  }

  function revokeConsent(): void {
    const now = new Date().toISOString()
    hasResponded.value = true
    analytics.value = false
    consentDate.value = now
    saveToStorage({ hasResponded: true, analytics: false, consentDate: now })
  }

  return { hasResponded, analytics, consentDate, needsBanner, grantConsent, revokeConsent }
})
